import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Route } from 'lucide-react';
import { SettingRow } from '@vnetwork/ui/layout';
import { Card, CardHeader, CardTitle, CardDescription, CardBody, Button, IconButton, Input, Switch, Textarea } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { useCdnConfig, useUpdateCdnConfig, useDeleteCdnConfig } from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { ConfigFormShell, jsonEqual } from '../../_shared/ConfigFormShell';
import { CdnAspectDrift } from '../../_shared/VendorDrift';

interface PathRule {
  _id: string;
  name: string;
  priority: number;
  enabled: boolean;
  match_path: string;
  match_methods: string[];
  requests_per_second: number;
  burst_size: number;
  by_ip: boolean;
}
interface Form {
  enabled: boolean;
  requests_per_second: number;
  burst_size: number;
  by_ip: boolean;
  bypass_ips: string[];
  bypass_header: string;
  bypass_header_values: string[];
  max_request_body_bytes: number;
  max_upload_rate_bps: number;
  min_upload_rate_bps: number;
  adaptive: boolean;
  custom_response_body: string;
  custom_response_content_type: string;
  rules: PathRule[];
}
let _rl = 0;
const lid = () => `rl${++_rl}`;
const N = (v: unknown, d: number) => (v == null ? d : Number(v));

const DEFAULTS: Form = {
  enabled: false, requests_per_second: 100, burst_size: 200, by_ip: true, bypass_ips: [], bypass_header: '', bypass_header_values: [],
  max_request_body_bytes: 0, max_upload_rate_bps: 0, min_upload_rate_bps: 0, adaptive: false, custom_response_body: '', custom_response_content_type: '', rules: [],
};

function hydrate(c: Record<string, unknown>): Form {
  const rules = Array.isArray(c.rules) ? (c.rules as Record<string, unknown>[]) : [];
  return {
    enabled: Boolean(c.enabled),
    requests_per_second: N(c.requests_per_second, 100),
    burst_size: N(c.burst_size, 200),
    by_ip: c.by_ip == null ? true : Boolean(c.by_ip),
    bypass_ips: Array.isArray(c.bypass_ips) ? (c.bypass_ips as string[]) : [],
    bypass_header: (c.bypass_header as string) || '',
    bypass_header_values: Array.isArray(c.bypass_header_values) ? (c.bypass_header_values as string[]) : [],
    max_request_body_bytes: N(c.max_request_body_bytes, 0),
    max_upload_rate_bps: N(c.max_upload_rate_bps, 0),
    min_upload_rate_bps: N(c.min_upload_rate_bps, 0),
    adaptive: Boolean(c.adaptive),
    custom_response_body: (c.custom_response_body as string) || '',
    custom_response_content_type: (c.custom_response_content_type as string) || '',
    rules: rules.map((r) => ({
      _id: lid(),
      name: (r.name as string) || '',
      priority: N(r.priority, 0),
      enabled: r.enabled == null ? true : Boolean(r.enabled),
      match_path: (r.match_path as string) || '',
      match_methods: Array.isArray(r.match_methods) ? (r.match_methods as string[]) : [],
      requests_per_second: N(r.requests_per_second, 10),
      burst_size: N(r.burst_size, 20),
      by_ip: r.by_ip == null ? true : Boolean(r.by_ip),
    })),
  };
}

export function RateLimitingTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data: cfg, isLoading } = useCdnConfig(domainId, 'rate-limit', { vendorMarkers: ['enabled', 'requests_per_second'] });
  const isConfigured = !!cfg;
  const [form, setForm] = useState<Form>(DEFAULTS);
  const [snap, setSnap] = useState('');
  useEffect(() => {
    const next = cfg ? hydrate(cfg) : DEFAULTS;
    setForm(next);
    setSnap(JSON.stringify(next));
  }, [cfg]);
  const dirty = useMemo(() => !jsonEqual(form, JSON.parse(snap || '{}')), [form, snap]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const patchRule = (id: string, c: Partial<PathRule>) => set('rules', form.rules.map((r) => (r._id === id ? { ...r, ...c } : r)));

  const update = useUpdateCdnConfig(domainId, 'rate-limit', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'rate_limit', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'Rate limiting saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save', description: errorMessage(e, 'Could not save') }),
  });
  const remove = useDeleteCdnConfig(domainId, 'rate-limit', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'rate_limit', 'delete');
      show({ tone: 'success', title: 'Rate limiting removed' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove', description: errorMessage(e, 'Could not remove') }),
  });
  const busy = update.isPending || remove.isPending;

  const save = () => {
    const { rules, ...defaults } = form;
    update.mutate({ ...defaults, rules: rules.map(({ _id, ...r }) => r) });
  };
  const numField = (label: string, key: keyof Form, hint?: string) => (
    <Field label={label} description={hint}>
      {(p) => <Input {...p} type="number" value={String(form[key])} onChange={(e) => set(key, (Number(e.target.value) || 0) as Form[typeof key])} />}
    </Field>
  );
  const grid: React.CSSProperties = { display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' };

  return (
    <ConfigFormShell
      title="Rate Limiting"
      description={`Request rate limits for ${domain}.`}
      isLoading={isLoading}
      isConfigured={isConfigured}
      dirty={dirty}
      busy={busy}
      saving={update.isPending}
      statusLabel={form.enabled ? 'Enabled' : undefined}
      onSave={save}
      onRemove={() => remove.mutate()}
      drift={<CdnAspectDrift domainId={domainId} path="rate-limit" />}
      onCancel={() => { if (snap) setForm(JSON.parse(snap)); }}
    >
      <Card>
        <CardHeader>
          <CardTitle>Domain defaults</CardTitle>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <SettingRow title="Enabled" control={<Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} />} />
          <div style={grid}>
            {numField('Requests / second', 'requests_per_second')}
            {numField('Burst size', 'burst_size')}
          </div>
          <SettingRow title="Rate limit by IP" control={<Switch checked={form.by_ip} onCheckedChange={(v) => set('by_ip', v)} />} />
          <SettingRow title="Adaptive rate limiting" control={<Switch checked={form.adaptive} onCheckedChange={(v) => set('adaptive', v)} />} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Limits &amp; bypass</CardTitle>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <div style={grid}>
            {numField('Max request body (bytes)', 'max_request_body_bytes')}
            {numField('Max upload (bps)', 'max_upload_rate_bps')}
            {numField('Min upload (bps)', 'min_upload_rate_bps')}
          </div>
          <Field label="Bypass IPs" description="One per line.">
            {(p) => <Textarea {...p} rows={2} value={form.bypass_ips.join('\n')} onChange={(e) => set('bypass_ips', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} />}
          </Field>
          <div style={grid}>
            <Field label="Bypass header name">{(p) => <Input {...p} value={form.bypass_header} onChange={(e) => set('bypass_header', e.target.value)} />}</Field>
            <Field label="Bypass header values" description="Comma-separated.">{(p) => <Input {...p} value={form.bypass_header_values.join(', ')} onChange={(e) => set('bypass_header_values', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />}</Field>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          actions={
            <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={() => set('rules', [...form.rules, { _id: lid(), name: '', priority: 100, enabled: true, match_path: '/', match_methods: [], requests_per_second: 10, burst_size: 20, by_ip: true }])}>
              Add rule
            </Button>
          }
        >
          <CardTitle>Per-path rules</CardTitle>
          <CardDescription>Override the defaults for specific paths.</CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {form.rules.length === 0 ? (
            <EmptyState size="sm" icon={<Route size={20} />} title="No per-path rules yet" description="Defaults apply to all paths." />
          ) : (
            form.rules.map((r) => (
              <div key={r._id} style={{ border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-3)', opacity: r.enabled ? 1 : 0.6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', fontSize: 'var(--vn-font-size-md)', cursor: 'pointer' }}>
                    <Switch size="sm" checked={r.enabled} onCheckedChange={(v) => patchRule(r._id, { enabled: v })} aria-label="Enabled" />
                    {r.enabled ? 'Enabled' : 'Disabled'}
                  </label>
                  <IconButton variant="ghost" size="sm" aria-label="Remove" onClick={() => set('rules', form.rules.filter((x) => x._id !== r._id))}>
                    <Trash2 size={15} />
                  </IconButton>
                </div>
                <div style={grid}>
                  <Field label="Name">{(p) => <Input {...p} value={r.name} onChange={(e) => patchRule(r._id, { name: e.target.value })} />}</Field>
                  <Field label="Match path">{(p) => <Input {...p} value={r.match_path} onChange={(e) => patchRule(r._id, { match_path: e.target.value })} />}</Field>
                  <Field label="Methods" description="Comma-separated.">{(p) => <Input {...p} value={r.match_methods.join(', ')} onChange={(e) => patchRule(r._id, { match_methods: e.target.value.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean) })} />}</Field>
                  <Field label="Priority">{(p) => <Input {...p} type="number" value={String(r.priority)} onChange={(e) => patchRule(r._id, { priority: Number(e.target.value) || 0 })} />}</Field>
                  <Field label="Requests / second">{(p) => <Input {...p} type="number" value={String(r.requests_per_second)} onChange={(e) => patchRule(r._id, { requests_per_second: Number(e.target.value) || 0 })} />}</Field>
                  <Field label="Burst size">{(p) => <Input {...p} type="number" value={String(r.burst_size)} onChange={(e) => patchRule(r._id, { burst_size: Number(e.target.value) || 0 })} />}</Field>
                </div>
                <SettingRow title="By IP" control={<Switch checked={r.by_ip} onCheckedChange={(v) => patchRule(r._id, { by_ip: v })} />} />
              </div>
            ))
          )}
        </CardBody>
      </Card>
    </ConfigFormShell>
  );
}
